import cn from 'classnames';
import { Color } from 'libs/end-to-end/src/types/branding/color';
import { isNullOrUndefined } from 'libs/end-to-end/src/is-null-or-undefined';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { PayCorrectionEmployeeItem } from 'apps/sigma2-ui/src/pay-correction/components/PayCorrectionTable';

export const MAX_BUBBLE_SIZE_IN_PX = 65;
export const MIN_BUBBLE_SIZE_IN_PX = 22;
export const SELECTED_BORDER_OFFSET = 10;
export const GRAY = '#9DA0A3';
export const BLACK = 'black';

export const getRatio = ({
  value,
  maxValue,
}: {
  value: number;
  maxValue: number;
}): number => {
  const isZero = value === 0 || maxValue === 0;

  if (isZero) {
    return 0;
  }
  return value / maxValue;
};

export const getBubbleSizeInPx = ({
  thisBubbleEmployeeCount,
  totalEmployeeCount,
}: {
  thisBubbleEmployeeCount: number;
  totalEmployeeCount: number;
}): number => {
  const ratio = getRatio({
    value: thisBubbleEmployeeCount,
    maxValue: totalEmployeeCount,
  });

  if (ratio === 0) {
    return MIN_BUBBLE_SIZE_IN_PX;
  }

  return Math.max(
    Math.round(ratio * MAX_BUBBLE_SIZE_IN_PX),
    MIN_BUBBLE_SIZE_IN_PX,
  );
};

export interface PayCorrectionEmployeeItemWithAdjustedSalary
  extends Omit<PayCorrectionEmployeeItem, 'id' | 'checked'> {
  adjusted_salary: number;
}

interface CountByLevel {
  [level: string]: number;
}

export const roundToStep = ({
  value,
  step,
}: {
  value: number;
  step: number;
}): number => {
  return Math.round(value / step) * step;
};

/**
 * Returns 0 if the value is less than 0. Or the value itself if it's greater than 0
 * Used to avoid negative values in the graph min/maxes
 */
const zeroOrGreater = (x: number): number => {
  return Math.max(0, x);
};

const isWithinInternalGoalRange = ({
  roundedInternalGoalMin,
  roundedInternalGoalMax,
  roundedAdjustedSalary,
}: {
  roundedInternalGoalMin: number | null;
  roundedInternalGoalMax: number | null;
  roundedAdjustedSalary: number;
}) => {
  if (
    isNullOrUndefined(roundedInternalGoalMin) ||
    isNullOrUndefined(roundedInternalGoalMax)
  ) {
    return false;
  }

  const result =
    roundedAdjustedSalary >= roundedInternalGoalMin &&
    roundedAdjustedSalary <= roundedInternalGoalMax;

  return result;
};

export interface PayGradeGraphDatum {
  x: number;
  y: 0;
  count: number;
  adjustedSalary: number;
  countByLevel: CountByLevel;
}

interface GetPayGradeGraphDataParams {
  internalGoalMin: number | null;
  internalGoalMax: number | null;
  employees: PayCorrectionEmployeeItemWithAdjustedSalary[];
  step: number;
}

export type PayGradeGraphData = {
  roundedGraphMin: number | null;
  roundedGraphMax: number | null;
  roundedInternalGoalMin: number | null;
  roundedInternalGoalMax: number | null;
  data: [
    {
      id: 'lessThanInternalGoal';
      color: typeof GRAY;
      data: PayGradeGraphDatum[];
    },
    {
      id: 'withinInternalGoal';
      color: typeof BLACK;
      data: PayGradeGraphDatum[];
    },
    {
      id: 'moreThanInternalGoal';
      color: typeof GRAY;
      data: PayGradeGraphDatum[];
    },
  ];
};

interface AdjustedSalaryToDatum {
  [adjustedSalary: number]: PayGradeGraphDatum;
}

// Creates a datum for a pay grade group that we'll later modify the counts on
const createPayGradeGraphDatum = ({
  roundedAdjustedSalary,
}: {
  roundedAdjustedSalary: number;
}): PayGradeGraphDatum => {
  return {
    x: roundedAdjustedSalary,
    y: 0,
    adjustedSalary: roundedAdjustedSalary,
    count: 0,
    countByLevel: {},
  };
};

const mergeEmployeeLevelCounts = ({
  existingCountByLevel,
  employee,
}: {
  existingCountByLevel: CountByLevel;
  employee: PayCorrectionEmployeeItemWithAdjustedSalary;
}): CountByLevel => {
  const mergedCountByLevel = { ...existingCountByLevel };

  if (!isNullOrUndefined(employee.race)) {
    const existingCountForRace = existingCountByLevel[employee.race] ?? 0;

    mergedCountByLevel[employee.race] = existingCountForRace + 1;
  }

  if (!isNullOrUndefined(employee.gender)) {
    const existingCountForGender = existingCountByLevel[employee.gender] ?? 0;

    mergedCountByLevel[employee.gender] = existingCountForGender + 1;
  }

  return mergedCountByLevel;
};

/**
 * Used to pad the min and max of the graph so the bubbles don't appear on the edge
 */
export const getGraphMinMaxPadding = ({ step }: { step: number }): number => {
  return step * 2;
};

/**
 * This function does the following:
 * 1. Groups the employees by their adjusted salary, rounded to the nearest step
 * 2. Sets the min and max for the graph and the internal goal
 * 3. Sets the count and count by dimension for each group
 * 4. Sets the color for each group
 *
 * It's a bit long, but it's only O(n) time complexity (three passes). Even though it could be done in one pass, I think it's easier to understand this way.
 */
export const getPayGradeGraphData = ({
  internalGoalMin,
  internalGoalMax,
  step,
  employees,
}: GetPayGradeGraphDataParams): PayGradeGraphData => {
  // Set the default values for each group in an internal state object
  const adjustedSalaryToDatum: AdjustedSalaryToDatum = {};

  const roundedInternalGoalMin = !isNullOrUndefined(internalGoalMin)
    ? zeroOrGreater(roundToStep({ value: internalGoalMin, step }))
    : null;

  const roundedInternalGoalMax = !isNullOrUndefined(internalGoalMax)
    ? zeroOrGreater(roundToStep({ value: internalGoalMax, step }))
    : null;

  // Set internal goal min and max if available
  [roundedInternalGoalMin, roundedInternalGoalMax].forEach(
    (roundedAdjustedSalary) => {
      if (!isNullOrUndefined(roundedAdjustedSalary)) {
        const datum = createPayGradeGraphDatum({ roundedAdjustedSalary });

        adjustedSalaryToDatum[roundedAdjustedSalary] = datum;
      }
    },
  );

  // Group employees by their adjusted salary into datums with their counts
  employees.forEach((employee) => {
    const adjustedSalary = roundToStep({
      step,
      value: employee.adjusted_salary,
    });

    const defaultDatum = createPayGradeGraphDatum({
      roundedAdjustedSalary: adjustedSalary,
    });

    const exstingDatum = adjustedSalaryToDatum[adjustedSalary] ?? defaultDatum;

    adjustedSalaryToDatum[adjustedSalary] = {
      ...exstingDatum,
      count: exstingDatum.count + 1,
      countByLevel: mergeEmployeeLevelCounts({
        existingCountByLevel: exstingDatum.countByLevel,
        employee,
      }),
    };
  });

  // Group the datums into the three groups: less than internal goal, within internal goal, and more than internal goal
  const lessThanInternalGoal: PayGradeGraphDatum[] = [];
  const withinInternalGoal: PayGradeGraphDatum[] = [];
  const moreThanInternalGoal: PayGradeGraphDatum[] = [];

  // If we have an internal goal, add a datum to connect the less than and more than groups to the internal goal so it's all one continuous line
  if (!isNullOrUndefined(roundedInternalGoalMin)) {
    const datum = createPayGradeGraphDatum({
      roundedAdjustedSalary: roundedInternalGoalMin,
    });

    lessThanInternalGoal.push(datum);
  }

  if (!isNullOrUndefined(roundedInternalGoalMax)) {
    const datum = createPayGradeGraphDatum({
      roundedAdjustedSalary: roundedInternalGoalMax,
    });

    moreThanInternalGoal.push(datum);
  }

  // Put each datum into the correct group
  Object.values(adjustedSalaryToDatum).forEach((datum) => {
    const isWithinInternalGoal = isWithinInternalGoalRange({
      roundedInternalGoalMin,
      roundedInternalGoalMax,
      // The datum's adjusted salary is already rounded to the nearest step
      roundedAdjustedSalary: datum.adjustedSalary,
    });

    if (isWithinInternalGoal) {
      withinInternalGoal.push(datum);
      return;
    }

    const isLessThanInternalGoal =
      roundedInternalGoalMin && datum.adjustedSalary < roundedInternalGoalMin;

    if (isLessThanInternalGoal) {
      lessThanInternalGoal.push(datum);
      return;
    }

    const isMoreThanInternalGoal =
      roundedInternalGoalMax && datum.adjustedSalary > roundedInternalGoalMax;

    if (isMoreThanInternalGoal) {
      moreThanInternalGoal.push(datum);
      return;
    }

    // At this point we know we don't have internalGoalMin/internalGoalMax. So we put in the isMoreThanInternalGoal group
    moreThanInternalGoal.push(datum);
  });

  let roundedGraphMin: number | null = null;
  let roundedGraphMax: number | null = null;

  // Get the min and max for the graph and pad them by 2 steps to ensure the bubbles are always in between the min and max and not on the edge
  Object.values(adjustedSalaryToDatum).forEach((datum) => {
    if (
      isNullOrUndefined(roundedGraphMin) ||
      datum.adjustedSalary < roundedGraphMin
    ) {
      roundedGraphMin = datum.adjustedSalary;
    }

    if (
      isNullOrUndefined(roundedGraphMax) ||
      datum.adjustedSalary > roundedGraphMax
    ) {
      roundedGraphMax = datum.adjustedSalary;
    }
  });

  if (!isNullOrUndefined(roundedGraphMin)) {
    roundedGraphMin = zeroOrGreater(
      roundedGraphMin - getGraphMinMaxPadding({ step }),
    );

    const datum = createPayGradeGraphDatum({
      roundedAdjustedSalary: roundedGraphMin,
    });

    // We have this logic to group everything in the moreThanInternalGoal group if we don't have an internal goal min
    if (!isNullOrUndefined(roundedInternalGoalMin)) {
      lessThanInternalGoal.push(datum);
    } else {
      moreThanInternalGoal.push(datum);
    }
  }

  if (!isNullOrUndefined(roundedGraphMax)) {
    roundedGraphMax = roundedGraphMax + getGraphMinMaxPadding({ step });

    const datum = createPayGradeGraphDatum({
      roundedAdjustedSalary: roundedGraphMax,
    });

    moreThanInternalGoal.push(datum);
  }

  return {
    roundedGraphMin,
    roundedGraphMax,
    roundedInternalGoalMin,
    roundedInternalGoalMax,
    data: [
      {
        id: 'lessThanInternalGoal',
        color: GRAY,
        data: lessThanInternalGoal.sort((a, b) => {
          return a.adjustedSalary - b.adjustedSalary;
        }),
      },
      {
        id: 'withinInternalGoal',
        color: BLACK,
        data: withinInternalGoal.sort((a, b) => {
          return a.adjustedSalary - b.adjustedSalary;
        }),
      },
      {
        id: 'moreThanInternalGoal',
        color: GRAY,
        data: moreThanInternalGoal.sort((a, b) => {
          return a.adjustedSalary - b.adjustedSalary;
        }),
      },
    ],
  };
};

export const defaultBubbleColors = {
  lessThanInternalGoal: {
    stroke: 'stroke-current text-raspberry-200 stroke-2 fill-transparent',
    fill: 'fill-current text-raspberry-600',
    selectedStroke:
      'stroke-current text-raspberry-600 stroke-2 fill-transparent',
  },
  withinInternalGoal: {
    default: {
      stroke: 'stroke-current text-gray-200 stroke-2 fill-transparent',
      fill: 'fill-current text-black',
      selectedStroke: 'stroke-current text-black stroke-2 fill-transparent',
    },
  },
};

/**
 * There's a lot going on here:
 * If a bubble is hovered, isReducedOpacity is true for the other bubbles
 * If a dimension button in the table is hovered, show all bubbles with at least one employee with that dimension as the dimension color. All other bubbles are black. No out of range colors are used.
 * If nothing is hovered, out of range bubbles are pink, and other bubbles are black
 */
export const getBubbleColor = ({
  isLessThanInternalGoal,
  variant,
  levelToColor,
  highlightedDimensionLevel,
  countByLevel,
  hasReducedOpacity,
  adjustedSalaryFilters,
  datum,
}: {
  isLessThanInternalGoal: boolean;
  variant: 'fill' | 'stroke' | 'selectedStroke';
  levelToColor: { [level: string]: Color };
  highlightedDimensionLevel: string | null;
  countByLevel: CountByLevel;
  hasReducedOpacity: boolean;
  adjustedSalaryFilters: number[];
  datum: Pick<PayGradeGraphDatum, 'adjustedSalary'>;
}): string => {
  const bubbleColorsWithDimensions = {
    ...defaultBubbleColors,
    wihinRange: {
      ...defaultBubbleColors.withinInternalGoal,
      ...levelToColor,
    },
  };

  const shouldUseOutOfInternalGoalColors =
    isLessThanInternalGoal && !highlightedDimensionLevel;

  if (shouldUseOutOfInternalGoalColors) {
    return cn(defaultBubbleColors.lessThanInternalGoal[variant], {
      'opacity-50': hasReducedOpacity,
    });
  }

  const hasDimensionColor =
    highlightedDimensionLevel && levelToColor[highlightedDimensionLevel];

  const levelCount =
    (highlightedDimensionLevel && countByLevel[highlightedDimensionLevel]) || 0;

  const shouldUseDimensionColors =
    hasDimensionColor &&
    shouldShowLevelCountRatio({
      datum,
      adjustedSalaryFilters,
      levelCount,
    });

  if (shouldUseDimensionColors) {
    const colorName: Color = levelToColor[highlightedDimensionLevel];

    return cn(ConfigColors[colorName].Primary[variant], {
      'opacity-50': hasReducedOpacity,
    });
  }

  return cn(bubbleColorsWithDimensions.withinInternalGoal.default[variant], {
    'opacity-50': hasReducedOpacity,
  });
};

export const shouldShowLevelCountRatio = ({
  adjustedSalaryFilters,
  datum,
  levelCount,
}: {
  adjustedSalaryFilters: number[];
  datum: Pick<PayGradeGraphDatum, 'adjustedSalary'>;
  levelCount: number;
}) => {
  const hasAdjustedSalaryFilters = adjustedSalaryFilters.length > 0;

  const adjustedSalaryIsIncludedInFilter = adjustedSalaryFilters.includes(
    datum.adjustedSalary,
  );

  const eitherNoAdjustedSalaryFiltersOrAdjustedSalaryIsIncludedInFilter =
    (hasAdjustedSalaryFilters && adjustedSalaryIsIncludedInFilter) ||
    !hasAdjustedSalaryFilters;

  return (
    levelCount >= 1 &&
    eitherNoAdjustedSalaryFiltersOrAdjustedSalaryIsIncludedInFilter
  );
};

/**
 * If there's only one item in the bubble, don't show the count
 * If there's more than one item in the bubble and no highlightedDimensionLevel, show the count
 * If there's more than one item in the bubble, a highlightedDimensionLevel, and the bubble has any employees with the highlightedDimensionLevel, show the count for the highlighted dimension / count
 */
export const getBubbleCountText = ({
  highlightedDimensionLevel,
  datum,
  adjustedSalaryFilters,
}: {
  highlightedDimensionLevel: string | null;
  datum: Pick<PayGradeGraphDatum, 'count' | 'countByLevel' | 'adjustedSalary'>;
  adjustedSalaryFilters: number[] /* adjustedSalaries */;
}): string => {
  if (datum.count <= 1) {
    return '';
  }

  if (highlightedDimensionLevel) {
    const levelCount = datum.countByLevel[highlightedDimensionLevel] ?? 0;

    if (
      shouldShowLevelCountRatio({
        levelCount,
        datum,
        adjustedSalaryFilters,
      })
    ) {
      return `${levelCount}/${datum.count}`;
    }
  }

  // This number will already be rounded to the nearest step
  return datum.count.toString();
};
